/**
 * Image history storage utilities for managing previously uploaded images
 */

import { storage } from './localStorage';

const STORAGE_KEYS = {
  IMAGE_HISTORY: 'pdf-editor-image-history',
} as const;

/**
 * Interface for stored image data
 */
export interface StoredImage {
  id: string;
  name: string;
  data: string; // Base64 data URL
  timestamp: number;
  size: number; // File size in bytes (estimated from base64)
}

/**
 * Interface for image history storage
 */
export interface ImageHistory {
  images: StoredImage[];
  totalSize: number;
}

/**
 * Maximum total size for image history (2MB to stay within localStorage limits)
 */
const MAX_HISTORY_SIZE = 2 * 1024 * 1024; // 2MB

/**
 * Maximum number of images to store
 */
const MAX_IMAGES = 20;

/**
 * Estimate the size of a base64 data URL in bytes
 */
const estimateBase64Size = (dataUrl: string): number => {
  // Remove data URL prefix and estimate actual size
  const base64Data = dataUrl.split(',')[1] || '';
  return Math.ceil(base64Data.length * 0.75); // Base64 encoding adds ~33% overhead
};

/**
 * Generate a unique ID for an image
 */
const generateImageId = (): string => {
  return `img_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Load image history from localStorage
 */
export const loadImageHistory = (): ImageHistory => {
  return storage.get(STORAGE_KEYS.IMAGE_HISTORY, { images: [], totalSize: 0 });
};

/**
 * Save image history to localStorage
 */
export const saveImageHistory = (history: ImageHistory): boolean => {
  return storage.set(STORAGE_KEYS.IMAGE_HISTORY, history);
};

/**
 * Add a new image to the history
 */
export const addImageToHistory = (name: string, data: string): StoredImage | null => {
  const imageSize = estimateBase64Size(data);
  
  // Check if image is too large
  if (imageSize > MAX_HISTORY_SIZE / 2) {
    console.warn('Image too large to store in history:', imageSize);
    return null;
  }

  const currentHistory = loadImageHistory();
  
  // Check if image already exists (same data)
  const existingImage = currentHistory.images.find(img => img.data === data);
  if (existingImage) {
    return existingImage;
  }

  const newImage: StoredImage = {
    id: generateImageId(),
    name,
    data,
    timestamp: Date.now(),
    size: imageSize,
  };

  // Make a copy of current images
  let updatedImages = [...currentHistory.images];
  let totalSize = currentHistory.totalSize + imageSize;

  // Remove oldest images if we exceed limits
  while ((totalSize > MAX_HISTORY_SIZE || updatedImages.length >= MAX_IMAGES) && updatedImages.length > 0) {
    const oldestImage = updatedImages.sort((a, b) => a.timestamp - b.timestamp)[0];
    updatedImages = updatedImages.filter(img => img.id !== oldestImage.id);
    totalSize -= oldestImage.size;
  }

  // Add new image
  updatedImages.push(newImage);
  totalSize += imageSize;

  const updatedHistory: ImageHistory = {
    images: updatedImages,
    totalSize,
  };

  if (saveImageHistory(updatedHistory)) {
    return newImage;
  }

  return null;
};

/**
 * Remove an image from the history
 */
export const removeImageFromHistory = (imageId: string): boolean => {
  const currentHistory = loadImageHistory();
  const imageToRemove = currentHistory.images.find(img => img.id === imageId);
  
  if (!imageToRemove) {
    return false;
  }

  const updatedImages = currentHistory.images.filter(img => img.id !== imageId);
  // Recalculate total size to ensure accuracy
  const newTotalSize = updatedImages.reduce((total, img) => total + img.size, 0);
  const updatedHistory: ImageHistory = {
    images: updatedImages,
    totalSize: newTotalSize,
  };

  return saveImageHistory(updatedHistory);
};

/**
 * Clear all images from history
 */
export const clearImageHistory = (): boolean => {
  return storage.remove(STORAGE_KEYS.IMAGE_HISTORY);
};

/**
 * Get image history statistics
 */
export const getImageHistoryStats = (): {
  count: number;
  totalSize: number;
  maxSize: number;
  usage: number; // Percentage
} => {
  const history = loadImageHistory();
  return {
    count: history.images.length,
    totalSize: history.totalSize,
    maxSize: MAX_HISTORY_SIZE,
    usage: (history.totalSize / MAX_HISTORY_SIZE) * 100,
  };
};